import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils";

// Colors that sit on top of artwork stay light-on-dark in every theme.
const ARTWORK_ON_MEDIA_COLOR = "#FFFFFF";
// Matches the portrait preset used for large character images elsewhere (profile hero, chat bubble).
const ARTWORK_AVATAR_PRESET = AvatarPreset.IMAGE_500X650;
const IMAGE_CLASS_NAME = "h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]";
const BLURRED_IMAGE_CLASS_NAME = "h-full w-full scale-110 object-cover blur-2xl";
const FALLBACK_CLASS_NAME = "absolute inset-0 flex items-center justify-center bg-gray-5";
const FALLBACK_INITIALS_CLASS_NAME = "select-none text-[34px] font-extrabold text-gray-9";

type DiscoverChatbotCardArtworkProps = {
  readonly name: string;
  readonly avatarUrl?: string;
  readonly isAvatarBlurred: boolean;
  readonly "data-testid": string;
};

const readInitials = (name: string): string => {
  const words = name.split(/[\s&]+/).filter(Boolean);
  return `${words[0]?.[0] ?? "?"}${words[1]?.[0] ?? ""}`.toUpperCase();
};

/**
 * Fills its parent with the chatbot avatar; falls back to initials on a missing or broken image.
 * Card-specific: unlike `DiscoverChatbotArtwork` (the profile overlay hero), this owns no sizing of
 * its own so it can fit whatever box the grid-driven card gives it.
 */
const DiscoverChatbotCardArtwork = ({ name, avatarUrl, isAvatarBlurred, "data-testid": testId }: DiscoverChatbotCardArtworkProps) => {
  const [stateIsImageFailed, setStateIsImageFailed] = useState(false);
  // Avatar URLs from the API are CDN-relative keys (e.g. "avatars/<id>.webp"); getAvatarUrl resolves
  // them against the assets host, matching how every other avatar image in the app renders.
  const resolvedAvatarUrl = useMemo(
    () => (avatarUrl ? (getAvatarUrl(avatarUrl, { preset: ARTWORK_AVATAR_PRESET }) ?? undefined) : undefined),
    [avatarUrl],
  );

  useEffect(() => {
    setStateIsImageFailed(false);
  }, [resolvedAvatarUrl]);

  const handleImageError = useCallback(() => setStateIsImageFailed(true), []);
  const isFallbackVisible = !resolvedAvatarUrl || stateIsImageFailed;

  return (
    <>
      {isFallbackVisible ? (
        <div data-testid={`${testId}-Fallback`} className={FALLBACK_CLASS_NAME}>
          <span className={FALLBACK_INITIALS_CLASS_NAME}>{readInitials(name)}</span>
        </div>
      ) : (
        <img
          src={resolvedAvatarUrl}
          alt=""
          className={isAvatarBlurred ? BLURRED_IMAGE_CLASS_NAME : IMAGE_CLASS_NAME}
          loading="lazy"
          onError={handleImageError}
          data-testid={testId}
        />
      )}

      {isAvatarBlurred ? (
        <div data-testid={`${testId}-NsfwVeil`} className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <Icon icon={IconVariants.EYE_OFF} size={IconSize.LARGE} color={ARTWORK_ON_MEDIA_COLOR} />
        </div>
      ) : null}
    </>
  );
};

export default React.memo(DiscoverChatbotCardArtwork);
